import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { fmtTime, todayKey, zonedDateKey } from "../lib/timezone";
import { CalendarClock, GraduationCap, UserCheck, CalendarOff, ChevronLeft, ChevronRight, Clock } from "lucide-react";

const ABS_LABEL = {
  Vacation: "Vacation", Holiday: "Holiday", "Call-In": "Call-In", Sick: "Sick",
  Training: "Training", "Personal Leave": "Personal Leave", Other: "Other",
};

function parseKey(key) { const [y, m, d] = key.split("-").map(Number); return new Date(y, m - 1, d); }
function keyLabel(key, opts) { return parseKey(key).toLocaleDateString(undefined, opts || { weekday: "long", month: "long", day: "numeric" }); }
function ymd(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function MySchedule() {
  const { user } = useAuth();
  const tz = user?.org_timezone;
  const [data, setData] = useState({ assignments: [], absences: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [weekAnchor, setWeekAnchor] = useState(new Date());
  const [monthAnchor, setMonthAnchor] = useState(new Date());
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    api.get("/schedule/mine").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const today = todayKey(tz);

  // assignments grouped by local date key
  const byKey = useMemo(() => {
    const m = {};
    data.assignments.forEach((a) => {
      const k = zonedDateKey(a.start, tz);
      (m[k] = m[k] || []).push(a);
    });
    Object.values(m).forEach((list) => list.sort((x, y) => (x.start || "").localeCompare(y.start || "")));
    return m;
  }, [data.assignments, tz]);

  // absence lookup: date key -> absence (covering ranges)
  const absenceForKey = (key) =>
    data.absences.find((ab) => ab.start_date <= key && key <= ab.end_date) || null;

  const roleLine = (a) => (a.schedule_role || "").trim();
  const timeRange = (a) => `${fmtTime(a.start, tz)} – ${fmtTime(a.end, tz)}`;
  const relLabel = (key) => {
    if (key === today) return "Today";
    if (key === ymd(addDays(parseKey(today), 1))) return "Tomorrow";
    return keyLabel(key, { weekday: "long", month: "short", day: "numeric" });
  };

  // next shift = earliest assignment whose end is in the future, skipping days the employee is absent
  const nextShift = useMemo(() => {
    const now = Date.now();
    const upcoming = [...data.assignments]
      .filter((a) => (a.end ? new Date(a.end).getTime() >= now : zonedDateKey(a.start, tz) >= today))
      .filter((a) => !data.absences.some((ab) => ab.start_date <= zonedDateKey(a.start, tz) && zonedDateKey(a.start, tz) <= ab.end_date))
      .sort((x, y) => (x.start || "").localeCompare(y.start || ""));
    return upcoming[0] || null;
  }, [data.assignments, data.absences, tz, today]);

  const TrainerTrainee = ({ a, compact }) => {
    if (a.schedule_role === "Trainee" && a.trainer_name) {
      return <p className={compact ? "text-xs text-slate-500" : "text-sm text-slate-600"} data-testid="mysched-trainer">
        <GraduationCap className="mr-1 inline h-3.5 w-3.5 text-blue-600" />Trainer: <span className="font-medium">{a.trainer_name}</span></p>;
    }
    return null;
  };

  const AbsencePill = ({ type }) => (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
      <CalendarOff className="h-3 w-3" /> {ABS_LABEL[type] || type}
    </span>
  );

  // ---------- UPCOMING ----------
  const upcomingList = useMemo(() => {
    const days = [];
    for (let i = 0; i < 21; i++) {
      const d = addDays(parseKey(today), i);
      const key = ymd(d);
      const shifts = byKey[key] || [];
      const abs = absenceForKey(key);
      if (shifts.length || abs) days.push({ key, shifts, abs });
    }
    return days;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byKey, data.absences, today]);

  // ---------- WEEK ----------
  const weekKeys = useMemo(() => {
    const start = addDays(weekAnchor, -weekAnchor.getDay());
    return [...Array(7)].map((_, i) => ymd(addDays(start, i)));
  }, [weekAnchor]);

  // ---------- MONTH ----------
  const monthCells = useMemo(() => {
    const y = monthAnchor.getFullYear(), m = monthAnchor.getMonth();
    const first = new Date(y, m, 1);
    const start = addDays(first, -first.getDay());
    return [...Array(42)].map((_, i) => {
      const d = addDays(start, i);
      return { key: ymd(d), day: d.getDate(), inMonth: d.getMonth() === m };
    });
  }, [monthAnchor]);

  if (loading) return <div className="h-64 animate-pulse rounded-md bg-slate-200/70" />;

  return (
    <div className="space-y-5" data-testid="my-schedule">
      {/* NEXT SHIFT CARD */}
      <Card className="overflow-hidden rounded-xl border-0 bg-gradient-to-br from-slate-900 to-slate-800 p-5 text-white shadow-lg" data-testid="next-shift-card">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-blue-300">
          <CalendarClock className="h-3.5 w-3.5" /> Next Shift
        </p>
        {nextShift ? (
          <div className="mt-2">
            <p className="text-sm font-medium text-slate-300">{relLabel(zonedDateKey(nextShift.start, tz))}</p>
            <p className="mt-0.5 font-display text-2xl font-black tracking-tight">{nextShift.shift_name}</p>
            <p className="mt-1 text-lg font-semibold text-blue-200">{timeRange(nextShift)}</p>
            {roleLine(nextShift) && <p className="mt-1 text-sm text-slate-300" data-testid="next-shift-role">Role: <span className="font-semibold text-white">{roleLine(nextShift)}</span></p>}
            {nextShift.schedule_role === "Trainee" && nextShift.trainer_name && (
              <p className="mt-1 text-sm text-slate-300" data-testid="next-shift-trainer">Training with: <span className="font-semibold text-white">{nextShift.trainer_name}</span></p>
            )}
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-300" data-testid="no-upcoming-shifts">No upcoming shifts scheduled.</p>
        )}
      </Card>

      {/* SUBVIEW TABS */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="mysched-tabs">
        {["upcoming", "week", "month"].map((t) => (
          <button key={t} data-testid={`mysched-tab-${t}`} onClick={() => setTab(t)}
            className={`rounded-md px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {t}
          </button>
        ))}
      </div>

      {/* UPCOMING */}
      {tab === "upcoming" && (
        <div className="space-y-2.5" data-testid="mysched-upcoming">
          {upcomingList.length === 0 ? (
            <Card className="rounded-md border border-slate-200 p-8 text-center text-sm text-slate-400">No shifts or absences in the next three weeks.</Card>
          ) : upcomingList.map(({ key, shifts, abs }) => (
            <Card key={key} data-testid={`mysched-day-${key}`} className={`rounded-lg border p-4 shadow-sm ${key === today ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{relLabel(key)}</p>
              {abs && <div className="mt-2"><AbsencePill type={abs.type} /></div>}
              {shifts.length ? (
                <div className="mt-2 space-y-2">
                  {abs && <p className="text-xs font-medium text-amber-700">Still on the roster this day:</p>}
                  {shifts.map((a) => (
                    <div key={a.id} className="border-l-2 border-blue-500 pl-3">
                      <p className="font-display text-base font-bold text-slate-900">{a.shift_name}</p>
                      <p className="text-sm text-slate-600">{timeRange(a)}</p>
                      {roleLine(a) && <p className="text-sm text-slate-500">Role: <span className="font-medium text-slate-700">{roleLine(a)}</span></p>}
                      <TrainerTrainee a={a} />
                    </div>
                  ))}
                </div>
              ) : !abs ? (
                <p className="mt-1.5 text-sm font-medium text-slate-400">Off</p>
              ) : null}
            </Card>
          ))}
        </div>
      )}

      {/* WEEK */}
      {tab === "week" && (
        <div data-testid="mysched-week">
          <div className="mb-3 flex items-center justify-between">
            <Button variant="outline" size="sm" data-testid="week-prev" onClick={() => setWeekAnchor((d) => addDays(d, -7))}><ChevronLeft className="h-4 w-4" /></Button>
            <p className="text-sm font-semibold text-slate-700">{keyLabel(weekKeys[0], { month: "short", day: "numeric" })} – {keyLabel(weekKeys[6], { month: "short", day: "numeric" })}</p>
            <Button variant="outline" size="sm" data-testid="week-next" onClick={() => setWeekAnchor((d) => addDays(d, 7))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-2">
            {weekKeys.map((key) => {
              const shifts = byKey[key] || [];
              const abs = absenceForKey(key);
              return (
                <Card key={key} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3.5 ${key === today ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"}`}>
                  <div className="min-w-[92px]">
                    <p className="text-sm font-bold text-slate-900">{keyLabel(key, { weekday: "long" })}</p>
                    <p className="text-xs text-slate-400">{keyLabel(key, { month: "short", day: "numeric" })}</p>
                  </div>
                  <div className="flex-1 text-right sm:text-left">
                    {abs && <div className="mb-1 sm:inline-block"><AbsencePill type={abs.type} /></div>}
                    {shifts.length ? (
                      <div className="space-y-1">
                        {shifts.map((a) => (
                          <div key={a.id}>
                            <span className="font-semibold text-slate-800">{a.shift_name}</span>
                            <span className="text-slate-500"> · {timeRange(a)}</span>
                            {roleLine(a) && <span className="text-slate-400"> · {roleLine(a)}</span>}
                          </div>
                        ))}
                      </div>
                    ) : !abs ? <span className="text-sm font-medium text-slate-400">Off</span> : null}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* MONTH */}
      {tab === "month" && (
        <div data-testid="mysched-month" className="mx-auto max-w-lg">
          <div className="mb-3 flex items-center justify-between">
            <Button variant="outline" size="sm" data-testid="month-prev" onClick={() => { setMonthAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1)); setSelectedKey(null); }}><ChevronLeft className="h-4 w-4" /></Button>
            <p className="text-sm font-semibold text-slate-700">{monthAnchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
            <Button variant="outline" size="sm" data-testid="month-next" onClick={() => { setMonthAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1)); setSelectedKey(null); }}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((d) => <div key={d} className="py-1 text-center text-[10px] font-bold uppercase text-slate-400">{d[0]}</div>)}
            {monthCells.map((c) => {
              const has = (byKey[c.key] || []).length > 0;
              const abs = absenceForKey(c.key);
              const isToday = c.key === today;
              const active = selectedKey === c.key;
              return (
                <button key={c.key} data-testid={`month-cell-${c.key}`} onClick={() => setSelectedKey(c.key)}
                  className={`flex aspect-square flex-col items-center justify-center rounded-md border text-sm transition-colors
                    ${!c.inMonth ? "border-transparent text-slate-300" : active ? "border-blue-500 bg-blue-50" : isToday ? "border-blue-300" : "border-slate-100 hover:bg-slate-50"}
                    ${c.inMonth ? "text-slate-700" : ""}`}>
                  <span className={`${isToday ? "font-black text-blue-700" : "font-medium"}`}>{c.day}</span>
                  {c.inMonth && (has || abs) && (
                    <span className="mt-0.5 flex gap-0.5">
                      {has && <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />}
                      {abs && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-2 flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Scheduled</span>
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Absence</span>
          </p>
          {selectedKey && (
            <Card className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="month-day-detail">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{keyLabel(selectedKey, { weekday: "long", month: "long", day: "numeric" })}</p>
              {(() => {
                const shifts = byKey[selectedKey] || [];
                const abs = absenceForKey(selectedKey);
                return (
                  <div className="mt-2 space-y-2">
                    {abs && <AbsencePill type={abs.type} />}
                    {shifts.length ? shifts.map((a) => (
                      <div key={a.id} className="border-l-2 border-blue-500 pl-3">
                        <p className="font-semibold text-slate-900">{a.shift_name}</p>
                        <p className="text-sm text-slate-600">{timeRange(a)}</p>
                        {roleLine(a) && <p className="text-sm text-slate-500">Role: <span className="font-medium text-slate-700">{roleLine(a)}</span></p>}
                        <TrainerTrainee a={a} />
                      </div>
                    )) : !abs ? <p className="text-sm font-medium text-slate-400">Off</p> : null}
                  </div>
                );
              })()}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
